//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { NavTabs } from 'unison';

import { isDefined, isNotNull } from '@utils/filterPredicates';
import {
  getSessionItem,
  removeSessionItems,
  setSessionItem,
} from '@utils/sessionUtils';

interface Props {
  name?: string;
  children: React.ReactNode;
}

export const Sidebar = ({ children, name }: Props) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Save scroll position when navigating away
  useLayoutEffect(() => {
    // Remove any other saved scroll positions for different sidebars
    removeSessionItems({
      prefix: 'sidebar-scroll-position',
      postfixToKeep: name,
    });

    if (!isDefined(name) || hasScrolled) {
      return;
    }

    const sidebar = sidebarRef.current;

    // Restore scroll position on mount
    if (sidebar) {
      const savedScrollTop = getSessionItem({
        key: 'sidebar-scroll-position',
        keyPostfix: name,
      });
      if (isNotNull(savedScrollTop)) {
        sidebar.scrollTop = savedScrollTop;
        setHasScrolled(true);
      }
    }

    // Clean up
    return () => {
      if (sidebar) {
        setSessionItem({
          key: { key: 'sidebar-scroll-position', keyPostfix: name },
          value: sidebar.scrollTop,
          expiresInMillis: 1000 * 60 * 60, // 1 hour
        });
      }
    };
  }, [sidebarRef]);

  return (
    <FullHeightPageFrameSidebar ref={sidebarRef}>
      <PageFrameSidebarContent>
        <FullHeightNavTabs direction='vertical'>{children}</FullHeightNavTabs>
      </PageFrameSidebarContent>
    </FullHeightPageFrameSidebar>
  );
};

const PageFrameSidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
`;

const FullHeightPageFrameSidebar = styled.div(
  ({ theme }) => `
    display: flex;
    flex: none;
    flex-direction: column;
    width: ${theme.smallMeasure};
    overflow-y: auto;
    background-color: ${theme.colors.white};
    border-right: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  `,
);

const FullHeightNavTabs = styled(NavTabs)`
  flex: 1;
`;

export default Sidebar;
